import { LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { Button, Flex, Form, Spin } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import {
  useTreatmentServiceGetTreatmentPlanEndpoint,
  useTreatmentServiceGetTreatmentPlanRecipeEndpoint
} from 'src/api/queries'
import { LoadingScreen, PageBreadcrumbs } from 'src/components'
import { RecipesBody, RecipesHeader } from 'src/features/persons/components'
import { usePersonBreadcrumb } from 'src/features/persons/hooks'
import { useLocales } from 'src/locales'
import { toDayjs } from 'src/utils'

export const PersonRecipesDetailsPage = () => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const { id, personId, treatmentPlanId } = useParams()

  const { breadcrumbItems, fullName, isLoadingPerson } =
    usePersonBreadcrumb(personId)

  const { data, isLoading } = useTreatmentServiceGetTreatmentPlanRecipeEndpoint(
    {
      id: String(treatmentPlanId),
      recipeId: String(id)
    },
    [String(id)],
    { enabled: !!id }
  )

  const { data: treatmentPlan, isLoading: isLoadingTreatment } =
    useTreatmentServiceGetTreatmentPlanEndpoint(
      {
        id: String(treatmentPlanId)
      },
      [String(treatmentPlanId)],
      { enabled: !!treatmentPlanId }
    )

  const onBackClick = () => {
    navigate(-1)
  }

  const printableRef = useRef<HTMLDivElement>(null)

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Application_${id}`
  })

  useEffect(() => {
    if (id && data && treatmentPlanId) {
      form.setFieldsValue({
        ...data,
        created: toDayjs(data?.created),
        endDate: data?.dispensingDate ? dayjs(data?.dispensingDate) : undefined,
        treatmentPlanId: data?.id,
        medicine: treatmentPlan?.prescribedTreatments?.map((item) => ({
          id: item?.id,
          name: item?.internationalName?.name,
          internationalNameId: item?.internationalName?.id,
          productFormId: {
            name: item?.productForm?.name,
            id: item?.productForm?.id
          },
          amountPerDay: item?.amount,
          amountToAdminister: item?.amountToAdminister,
          durationInDays: item?.durationInDays,
          totalAdministered: item?.totalAdministered
        })),
        recipesMedicine: data?.medicationDispenses?.map((item) => ({
          name: item?.internalInventoryItem?.product?.name,
          internationalNameId: item?.internalInventoryItem?.id,
          productFormId: item?.internalInventoryItem?.productForm?.name,
          dispensedAmount: item?.dispensedAmount
        }))
      })
    }
  }, [data, id, treatmentPlanId])

  if (isLoading || isLoadingPerson || isLoadingTreatment) {
    return <LoadingScreen />
  }

  return (
    <Spin spinning={isLoadingPerson}>
      <PageBreadcrumbs title={fullName} items={breadcrumbItems}>
        <Flex vertical gap={24}>
          <Flex justify="space-between">
            <Button
              icon={<LeftOutlined />}
              className="fit-content"
              onClick={onBackClick}
            >
              {t('common.back')}
            </Button>

            <Flex gap={16}>
              <Button icon={<PrinterOutlined />} onClick={() => onPrint()}>
                {t('stock_applications.print')}
              </Button>
            </Flex>
          </Flex>

          <Form form={form} layout="vertical" disabled>
            <Flex vertical gap={16} ref={printableRef}>
              <RecipesHeader data={data} />
              <RecipesBody form={form} />
            </Flex>
          </Form>
        </Flex>
      </PageBreadcrumbs>
    </Spin>
  )
}
